import { createAsyncThunk } from "@reduxjs/toolkit";

import { ACTIVITY_TYPES } from "Constants/constants";
import logger from "Libs/logger";
import {
  isJson,
  getEnvironmentDescriptionId,
  getIntegrationDescriptionId
} from "Libs/utils";
import { loadEnvironment } from "Reducers/environment";
import {
  loadDeploymentCurrent,
  loadDeploymentNext
} from "Reducers/environment/deployment";
import { orgByProjectIdSelector } from "Reducers/project/project";
import { AsyncThunkOptionType } from "Reducers/types";

import { configureResourcesNotification } from "../notifications/configureResources.notification";

import type { Activity } from "Api/activity/types";

// Activities whose completion creates or replaces the current deployment.
//
// Two reasons this list has to be complete. Fresh project creation (DP-644):
// the Redux environment record is stale (`has_code`/`has_deployment: false`)
// and nothing else refreshes it, so both the deployment AND the environment
// record must be re-fetched here or the overview panels stay empty until a
// full page reload. And since CON-1655 the deployment endpoints are asked once
// per environment, so the mount dispatches no longer paper over a missing
// entry: an activity absent from this list leaves the service tree, allocated
// resources and relationships showing the pre-change topology for the rest of
// the session.
//
// This thunk is the reliable refresh path — it runs for every activity on
// every page. `useProjectPage`'s listener overlaps with it but only fires when
// the sizing API is enabled, so it cannot be the only cover.
const DEPLOYMENT_CHANGING_TYPES: string[] = [
  ACTIVITY_TYPES.ENVIRONMENT.PUSH,
  ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE,
  ACTIVITY_TYPES.ENVIRONMENT.DEPLOY,
  // A manual redeploy, and what a variable change produces.
  ACTIVITY_TYPES.ENVIRONMENT.REDEPLOY,
  ACTIVITY_TYPES.ENVIRONMENT.MAINTENANCE_REDEPLOY,
  // Rewrite the target environment's deployment.
  ACTIVITY_TYPES.ENVIRONMENT.MERGE,
  ACTIVITY_TYPES.ENVIRONMENT.SYNCHRONIZE,
  ACTIVITY_TYPES.ENVIRONMENT.RESTORE,
  ACTIVITY_TYPES.ENVIRONMENT.BRANCH,
  ACTIVITY_TYPES.ENVIRONMENT.RESOURCES.UPDATE
];

type ActivityAcc = {
  activity: Activity;
  organizationDescriptionId: string;
  projectDescriptionId: string;
  environmentDescriptionId: string;
  integrationDescriptionId?: string;
}[];

export const loadActivitySuccess = createAsyncThunk<
  ActivityAcc,
  { activity: Activity },
  AsyncThunkOptionType
>(
  "app/activity/load",
  (
    { activity },
    { dispatch, getState, rejectWithValue, extra: { getIntl } }
  ) => {
    const { project, environments, state: activityState, type } = activity;

    const projectDescriptionId = project;
    const state = getState();
    const organizationDescriptionId = orgByProjectIdSelector(state, {
      projectId: projectDescriptionId
    });
    if (activity && organizationDescriptionId && projectDescriptionId) {
      configureResourcesNotification({
        activity,
        organizationId: organizationDescriptionId,
        projectId: projectDescriptionId,
        dispatch,
        intl: getIntl(),
        state
      });
    }

    const integrationDescriptionId = getIntegrationDescriptionId(
      getState,
      projectDescriptionId,
      activity?.integration
    );

    const reloadDeployment = (environmentId: string) => {
      // Guard on the org id too: loadEnvironment has no `condition`, so a
      // falsy `organizationDescriptionId` (project not yet in the
      // orgByProjectId map) would write the healed record to
      // `data[undefined][...]`, a slot the panel — keyed by the route org —
      // never reads. loadDeploymentCurrent self-vetoes on a falsy org, so
      // skipping both keeps the refresh consistent (DP-644).
      if (
        !organizationDescriptionId ||
        !DEPLOYMENT_CHANGING_TYPES.includes(type) ||
        activityState !== "complete"
      ) {
        return;
      }

      dispatch(
        loadDeploymentCurrent({
          organizationId: organizationDescriptionId,
          projectId: project,
          environmentId,
          settings: {
            escapeLoading: true,
            // The activity just created the deployment this environment was
            // missing, so the stored 404 must be replaced (DP-644, CON-1655).
            reload: true
          }
        })
      );
      // The next deployment changes with the current one, and it is what the
      // Configure resources form and the resources sticker read. The guard
      // blocks every non-reload dispatch, and the other two reload paths are
      // conditional — `useProjectPage`'s listener needs the sizing API on and
      // the hook mounted, `useReloadDeployment` needs the activity row
      // rendered — so this has to refresh it too (CON-1655). The thunk
      // self-vetoes when the sizing API is off.
      dispatch(
        loadDeploymentNext({
          organizationId: organizationDescriptionId,
          projectId: project,
          environmentId,
          settings: { reload: true }
        })
      );
      dispatch(
        loadEnvironment({
          organizationId: organizationDescriptionId,
          projectId: project,
          environmentId
        })
      );
    };

    try {
      return environments.reduce<ActivityAcc>((acc, environmentId) => {
        // Refresh before the in-store guard: right after fresh project
        // creation the environments list isn't in Redux yet, so the first
        // deploy-creating activity would otherwise skip the refresh and the
        // panels would wait for a later activity (DP-644). reloadDeployment
        // needs only org + project + the raw environment id from the activity.
        reloadDeployment(environmentId);

        const environmentDescriptionId = getEnvironmentDescriptionId(
          getState,
          organizationDescriptionId ?? "",
          projectDescriptionId,
          environmentId
        );
        if (!environmentDescriptionId) return acc;

        acc.push({
          activity,
          organizationDescriptionId,
          projectDescriptionId,
          environmentDescriptionId,
          integrationDescriptionId
        });
        return acc;
      }, []);
    } catch (error: any) {
      if (![404, 403, 400].includes(error.code ?? error.status ?? 0)) {
        const errorMessage = isJson(error)
          ? error
          : "An error occurred while attempting to load activity.";
        logger(errorMessage, {
          action: "app/activity/load_failure",
          meta: {
            organizationDescriptionId,
            projectDescriptionId
          }
        });
      }
      return rejectWithValue(error);
    }
  }
);
